// Local run/sweep history backed by IndexedDB. Keeps at most the most recent 50
// measurements so the Compare page and the history list can reload them without
// a network round-trip. Everything degrades gracefully when IndexedDB is
// unavailable (private windows, older browsers): reads return [], writes no-op.

import type { SharePayload } from './share';

const DB_NAME = 'plm-history';
const STORE = 'runs';
const DB_VERSION = 1;
const MAX_ENTRIES = 50;

/** A stored history entry: a payload plus a stable local id and share status. */
export interface HistoryEntry {
  id: string;
  createdAt: number;
  payload: SharePayload;
  /** Coordinator share id, once the entry has been uploaded. */
  shareId?: string;
}

function idbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' });
        os.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
}

function tx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = run(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB request failed'));
  });
}

/** Generate a stable local id. */
export function newHistoryId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `h-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** List history entries, newest first. Returns [] when storage is unavailable. */
export async function listHistory(): Promise<HistoryEntry[]> {
  if (!idbAvailable()) return [];
  try {
    const db = await openDB();
    const all = await tx<HistoryEntry[]>(db, 'readonly', (s) => s.getAll() as IDBRequest<HistoryEntry[]>);
    db.close();
    return all.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

/** Fetch a single history entry by id. */
export async function getHistory(id: string): Promise<HistoryEntry | undefined> {
  if (!idbAvailable()) return undefined;
  try {
    const db = await openDB();
    const entry = await tx<HistoryEntry | undefined>(
      db,
      'readonly',
      (s) => s.get(id) as IDBRequest<HistoryEntry | undefined>,
    );
    db.close();
    return entry;
  } catch {
    return undefined;
  }
}

/** Save a payload to history, pruning to the most recent MAX_ENTRIES. */
export async function saveHistory(entry: HistoryEntry): Promise<void> {
  if (!idbAvailable()) return;
  try {
    const db = await openDB();
    await tx(db, 'readwrite', (s) => s.put(entry));
    const all = await tx<HistoryEntry[]>(db, 'readonly', (s) => s.getAll() as IDBRequest<HistoryEntry[]>);
    if (all.length > MAX_ENTRIES) {
      const stale = all.sort((a, b) => a.createdAt - b.createdAt).slice(0, all.length - MAX_ENTRIES);
      for (const s of stale) {
        await tx(db, 'readwrite', (os) => os.delete(s.id));
      }
    }
    db.close();
  } catch {
    // Best-effort: history is a convenience, not a source of truth.
  }
}

/** Update the share id on an existing entry (after uploading to the server). */
export async function markShared(id: string, shareId: string): Promise<void> {
  const entry = await getHistory(id);
  if (!entry) return;
  await saveHistory({ ...entry, shareId });
}

/** Delete a history entry. */
export async function deleteHistory(id: string): Promise<void> {
  if (!idbAvailable()) return;
  try {
    const db = await openDB();
    await tx(db, 'readwrite', (s) => s.delete(id));
    db.close();
  } catch {
    // ignore
  }
}

/** Clear the entire history. */
export async function clearHistory(): Promise<void> {
  if (!idbAvailable()) return;
  try {
    const db = await openDB();
    await tx(db, 'readwrite', (s) => s.clear());
    db.close();
  } catch {
    // ignore
  }
}
